import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useContext, useMemo } from 'react';
import { WebElement } from 'z3k-config';
import { hasModbusSupport } from 'z3k-parser/model/device-spec/z3k-feature-flags';
import { SettingContainer } from 'console/device-settings/z3k/components/material-utils/settings-card/container';
import { SettingFullRow } from 'console/device-settings/z3k/components/material-utils/settings-card/full-row';
import { useEditingProps, useEditItem, useGetZ3kObject } from '../utils/unpack-props';
import { MaterialDropdown, MaterialObjectSelection, MaterialPhysicalInputOutputSelection, MaterialRadioGroupControl, } from 'console/shared/components/fields';
import { FlagSelector } from 'console/device-settings/z3k/components/material-utils/flag-selector';
import { complex_button_options, reactionOptions, } from 'console/device-settings/z3k/components/utils/z3k-selectors';
import { analog_input_options, analog_output_options, device_output_options, } from 'z3k-parser/lib/z3k-selectors';
import { ErrorBoundary } from 'shared/components/error-boundary';
import { SettingCard } from '../material-utils/settings-card/card';
import { Z3KSettings, Z3KSettingsEditor, } from 'console/device-settings/z3k/provider/types';
import { isMediumUI } from 'shared/app/lib/choose-ui-type';
import { removeNulls } from 'utils';
import { Control } from 'console/shared/components/fields/control';
import { TextInputWrapper } from 'console/shared/components/fields/text';
import { web_element_icons } from 'console/shared/components/fields/icon/icons';
import { IconsLibrary } from 'console/shared/components/fields/icon';
import { ControlWithoutValidation } from 'console/shared/components/fields/control/without-validation';
import { ControlWithoutLabel } from 'shared/components/fields/control/without-label';
import { AccordionWithValidation } from 'z3k-settings/components/material-utils/accordion-with-validation';
import { LabelWithIcon } from 'shared/components/fields/label-with-icon';
import { getIconIdByAliceView } from 'shared/components/fields/icon/icons/utils';
export const WebElementComponent = memo(function WebElementComponent() {
    const { config, replace_device_ui_settings } = useContext(Z3KSettingsEditor);
    const isMobile = !isMediumUI();
    const z3k_name = 'web_elements';
    const { deviceFirmwareVersion, device_ui_settings } = useContext(Z3KSettings);
    const deviceOutput_options = device_output_options(config);
    const complexButton_options = complex_button_options(config);
    const actions_options = reactionOptions(config);
    const outputs_options = analog_output_options(config);
    const inputs_options = analog_input_options(config);
    const web_element = useGetZ3kObject(z3k_name);
    const onChange = useEditItem(web_element.id, z3k_name);
    const is_status_type = useMemo(() => WebElement.isStatusType(web_element), [web_element]);
    const is_simple_type = useMemo(() => WebElement.isSimpleType(web_element), [web_element]);
    const is_complex_type = useMemo(() => WebElement.isComplexType(web_element), [web_element]);
    const is_analog_type = useMemo(() => WebElement.isAnalogType(web_element), [web_element]);
    const _onChangeType = useCallback((new_type) => {
        if (new_type === WebElement.Type.SIMPLE) {
            return onChange({
                ...web_element,
                type: new_type,
                id_active: is_status_type ? null : web_element.id_active,
                label2: '',
                id_passive: null,
            });
        }
        if (new_type === WebElement.Type.STATUS) {
            return onChange({
                ...web_element,
                type: new_type,
                id_active: null,
                id_passive: null,
            });
        }
        if (new_type === WebElement.Type.COMPLEX) {
            return onChange({
                ...web_element,
                type: new_type,
                id_active: is_status_type ? null : web_element.id_active,
                id_passive: null,
            });
        }
        return onChange({
            ...web_element,
            type: new_type,
            id_passive: null,
        });
    }, [onChange, web_element, is_status_type]);
    const name_editing_props = useEditingProps(web_element.id, z3k_name, 'name');
    const type_editing_props = useEditingProps(web_element.id, z3k_name, 'type');
    const label_1_editing_props = useEditingProps(web_element.id, z3k_name, 'label1');
    const label_2_editing_props = useEditingProps(web_element.id, z3k_name, 'label2');
    const alice_view_editing_props = useEditingProps(web_element.id, z3k_name, 'alice_view');
    const id_active_editing_props = useEditingProps(web_element.id, z3k_name, 'id_active');
    const id_passive_editing_props = useEditingProps(web_element.id, z3k_name, 'id_passive');
    const type_options = useMemo(() => removeNulls([
        {
            text: 'Статус входа/выхода',
            value: WebElement.Type.STATUS,
        },
        {
            text: 'Простая кнопка',
            value: WebElement.Type.SIMPLE,
        },
        {
            text: 'Сложная кнопка',
            value: WebElement.Type.COMPLEX,
        },
        hasModbusSupport(config.device)
            ? {
                text: 'Аналоговый регулятор',
                value: WebElement.Type.ANALOG,
            }
            : null,
    ]), [config.device]);
    const type_field = useCallback((input_props) => (_jsx(MaterialDropdown, { ...input_props, options: type_options })), [type_options]);
    const is_set_output_state = useMemo(() => (WebElement.isSetOutputState(web_element) ? '1' : '0'), [web_element]);
    const onChangeStatusIndication = useCallback((value) => {
        onChange({
            ...WebElement.setOutputState(web_element, value === '1'),
            id_active: null,
        });
    }, [onChange]);
    const radio_control_values = useMemo(() => [
        {
            label: 'Входа',
            value: '0',
            help_id: null,
        },
        {
            label: 'Выхода',
            value: '1',
            help_id: null,
        },
    ], []);
    const radio_field = useCallback((input_props) => (_jsx(MaterialRadioGroupControl, { control_values: radio_control_values, ...input_props })), [actions_options, is_complex_type]);
    const onChangeSettingRegister = useCallback((new_params) => onChange({ ...web_element, setting_register: new_params }), [onChange, web_element]);
    const web_element_icon = useMemo(() => device_ui_settings?.icons?.web_elements?.[web_element.id], [device_ui_settings?.icons?.web_elements, web_element.id]);
    const onChangeWebElementIcon = useCallback((icon_id) => {
        const new_ui_settings = {
            ...device_ui_settings,
            icons: {
                ...device_ui_settings?.icons,
                web_elements: {
                    ...(device_ui_settings?.icons?.web_elements ?? {}),
                    [web_element.id]: icon_id,
                },
            },
        };
        replace_device_ui_settings(new_ui_settings);
    }, [device_ui_settings, web_element.id]);
    const onChangeAliceView = useCallback((value) => {
        const alice_view = parseInt(value, 10);
        const setter = {
            alice_view,
        };
        onChange(setter);
        onChangeWebElementIcon(getIconIdByAliceView(alice_view));
    }, [onChange, web_element, onChangeWebElementIcon]);
    const alice_view_values = useMemo(() => [
        {
            label: 'Управление светом',
            value: WebElement.AliceView.Light.toString(),
            help_id: 'web_elements_alice_view_light',
        },
        {
            label: 'Открыть-закрыть',
            value: WebElement.AliceView.Openable.toString(),
            help_id: 'web_elements_alice_view_openable',
        },
        {
            label: 'Розетка',
            value: WebElement.AliceView.Socket.toString(),
            help_id: 'web_elements_alice_view_socket',
        },
        {
            label: 'Выключатель',
            value: WebElement.AliceView.Switch.toString(),
            help_id: 'web_elements_alice_view_switch',
        },
        {
            label: 'Вентиляция',
            value: WebElement.AliceView.Ventilation.toString(),
            help_id: 'web_elements_alice_view_ventilation',
        },
        {
            label: 'Не выбрано',
            value: WebElement.AliceView.NOT_CHOSEN.toString(),
            help_id: 'web_elements_alice_view_not_chosen',
        },
    ], []);
    const alice_view_field = useCallback((input_props) => (_jsx(MaterialRadioGroupControl, { control_values: alice_view_values, ...input_props })), [alice_view_values]);
    return (_jsx(ErrorBoundary, { children: _jsxs(SettingContainer, { children: [_jsx(Control, { label: "\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435", field: TextInputWrapper, help_id: "web_elements_name", ...name_editing_props }), _jsx(Control, { label: "\u0422\u0438\u043F \u044D\u043B\u0435\u043C\u0435\u043D\u0442\u0430", field: type_field, help_id: "web_elements_type", ...type_editing_props, onChange: _onChangeType }), is_status_type && (_jsxs(_Fragment, { children: [_jsx(ControlWithoutValidation, { label: "\u0418\u043D\u0434\u0438\u043A\u0430\u0446\u0438\u044F \u0441\u0442\u0430\u0442\u0443\u0441\u0430", help_id: "web_elements_id_analog", value: is_set_output_state, onChange: onChangeStatusIndication, field: radio_field }), is_set_output_state === '0' && (_jsx(MaterialObjectSelection, { label: "\u0412\u0445\u043E\u0434 \u0434\u043B\u044F \u0438\u043D\u0434\u0438\u043A\u0430\u0446\u0438\u0438", help_id: "web_elements_id_active", options: inputs_options, ...id_active_editing_props })), is_set_output_state === '1' && (
                        //@ts-ignore
                        _jsx(MaterialPhysicalInputOutputSelection, { label: "\u0412\u044B\u0445\u043E\u0434 \u0434\u043B\u044F \u0438\u043D\u0434\u0438\u043A\u0430\u0446\u0438\u0438", help_id: "web_elements_id_active", options: deviceOutput_options, ...id_active_editing_props }))] })), !is_analog_type && (_jsxs(SettingFullRow, { display: "flex", flexWrap: isMobile ? 'wrap' : 'nowrap', children: [_jsxs(SettingCard, { title: "\u041D\u0435\u0430\u043A\u0442\u0438\u0432\u043D\u043E\u0435 \u0441\u043E\u0441\u0442\u043E\u044F\u043D\u0438\u0435", children: [!is_simple_type && (_jsx(SettingFullRow, { children: _jsx(Control, { label: is_status_type
                                            ? 'Текст статуса'
                                            : 'Текст неактивной кнопки', help_id: is_status_type
                                            ? 'web_elements_label2_status'
                                            : 'web_elements_label2_complex', field: TextInputWrapper, ...label_2_editing_props }) })), (is_simple_type || is_complex_type) && (_jsx(SettingFullRow, { children: _jsx(MaterialObjectSelection, { label: "\u0414\u0435\u0439\u0441\u0442\u0432\u0438\u0435", help_id: is_simple_type
                                            ? 'web_elements_id_active_simple'
                                            : 'web_elements_id_active_complex', options: is_complex_type
                                            ? complexButton_options
                                            : actions_options, ...id_active_editing_props }) }))] }), _jsxs(SettingCard, { title: "\u0410\u043A\u0442\u0438\u0432\u043D\u043E\u0435 \u0441\u043E\u0441\u0442\u043E\u044F\u043D\u0438\u0435", children: [_jsx(SettingFullRow, { children: _jsx(Control, { label: is_status_type
                                            ? 'Текст статуса'
                                            : 'Текст активной кнопки', help_id: is_status_type
                                            ? 'web_elements_label1_status'
                                            : is_simple_type
                                                ? 'web_elements_label1_simple'
                                                : 'web_elements_label1_complex', field: TextInputWrapper, ...label_1_editing_props }) }), is_complex_type && (_jsx(SettingFullRow, { children: _jsx(MaterialObjectSelection, { label: "\u0414\u0435\u0439\u0441\u0442\u0432\u0438\u0435", help_id: "web_elements_id_passive_complex", options: is_complex_type
                                            ? complexButton_options
                                            : actions_options, ...id_passive_editing_props }) }))] })] })), is_analog_type && (_jsx(MaterialObjectSelection, { label: "\u0410\u043D\u0430\u043B\u043E\u0433\u043E\u0432\u044B\u0439 \u0432\u044B\u0445\u043E\u0434", help_id: "web_elements_id_analog", options: outputs_options, ...id_active_editing_props })), is_status_type && (_jsx(FlagSelector, { value: web_element.setting_register, onChange: onChangeSettingRegister, flag_label: "\u0418\u043D\u0432\u0435\u0440\u0441\u0438\u044F \u0438\u043D\u0434\u0438\u043A\u0430\u0442\u043E\u0440\u0430 \u0441\u043E\u0441\u0442\u043E\u044F\u043D\u0438\u044F", flag_help_id: "web_elements_inverse", mask: WebElement.Flags.INVERSION_FLAG })), deviceFirmwareVersion >= 122 &&
                    (is_complex_type || is_analog_type) && (_jsx(FlagSelector, { value: web_element.setting_register, onChange: onChangeSettingRegister, flag_label: "\u0421\u043E\u0445\u0440\u0430\u043D\u044F\u0442\u044C \u0441\u043E\u0441\u0442\u043E\u044F\u043D\u0438\u0435 \u0432 \u044D\u043D\u0435\u0440\u0433\u043E\u043D\u0435\u0437\u0430\u0432\u0438\u0441\u0438\u043C\u043E\u0439 \u043F\u0430\u043C\u044F\u0442\u0438", flag_help_id: "energy_independent_memory", mask: WebElement.Flags.FRAM_SAVED_STATE_FLAG })), _jsx(FlagSelector, { value: web_element.setting_register, onChange: onChangeSettingRegister, flag_label: "\u0421\u043A\u0440\u044B\u0432\u0430\u0442\u044C \u0432\u0438\u0434\u0436\u0435\u0442 \u043D\u0430 \u043F\u0430\u043D\u0435\u043B\u0438 \u0441\u043E\u0441\u0442\u043E\u044F\u043D\u0438\u044F", flag_help_id: "", mask: WebElement.Flags.HIDDEN_CARD_FLAG }), _jsx(FlagSelector, { value: web_element.setting_register, onChange: onChangeSettingRegister, flag_label: "\u0421\u043A\u0440\u044B\u0432\u0430\u0442\u044C \u043D\u0430\u0437\u0432\u0430\u043D\u0438\u0435", flag_help_id: "", mask: WebElement.Flags.HIDDEN_LABEL_FLAG }), !is_analog_type && (_jsx(IconsLibrary, { value: web_element_icon || 0, onChange: onChangeWebElementIcon, icons: web_element_icons })), deviceFirmwareVersion >= 450 && (_jsx(SettingFullRow, { children: _jsx(AccordionWithValidation, { header: _jsx(LabelWithIcon, { has_error: false, has_warning: false, help_id: "web_elements_alice_view", label: "\u0422\u0438\u043F \u044D\u043B\u0435\u043C\u0435\u043D\u0442\u0430 \u0432 \u0443\u043C\u043D\u043E\u043C \u0434\u043E\u043C\u0435 \u0410\u043B\u0438\u0441\u044B" }), validation_result: alice_view_editing_props.validation_result, validation_warnings_result: alice_view_editing_props.validation_warnings_result, sx: {
                            width: '100%',
                        }, children: _jsx(ControlWithoutLabel, { value: (web_element.alice_view ?? 0).toString(), onChange: onChangeAliceView, field: alice_view_field, validation_result: null, validation_warnings_result: null }) }) }))] }) }));
});
